import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Check, Crown, Zap, Star, CreditCard } from 'lucide-react';

const PLANS = [
  {
    id: 'monthly',
    name: 'Monthly Pass',
    price: 9.99,
    interval: 'month',
    features: [
      'Watch all live games',
      'Access to game archives',
      'HD streaming quality',
      'Cancel anytime'
    ],
    popular: false
  },
  {
    id: 'yearly',
    name: 'Annual Pass',
    price: 99.99,
    interval: 'year',
    features: [
      'Watch all live games',
      'Access to game archives',
      'HD streaming quality',
      '2 months FREE',
      'Priority support'
    ],
    popular: true
  }
];

export const SubscriptionPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    // Check for subscription return
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      checkSubscriptionStatus(sessionId);
    }
  }, []);

  const checkSubscriptionStatus = async (sessionId) => {
    try {
      const response = await api().get(`/subscriptions/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Subscription activated!');
        setSubscription({ active: true });
      }
    } catch (error) {
      console.error('Failed to check subscription:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      const response = await api().post('/subscriptions/checkout', null, {
        params: { plan_id: planId, origin_url: window.location.origin }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, interval) => {
    if (interval === 'year') {
      return `$${(price / 12).toFixed(2)}/mo`;
    }
    return `$${price.toFixed(2)}/${interval}`;
  };

  return (
    <div data-testid="subscription-page" className="max-w-4xl mx-auto space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 text-yellow-400 mb-4">
          <Crown className="w-6 h-6" />
          <span className="font-bold uppercase tracking-wider">Premium Access</span>
        </div>
        <h1 className="text-4xl font-heading font-bold uppercase tracking-wide mb-4">
          Watch Every Game Live
        </h1>
        <p className="text-zinc-400 text-lg max-w-xl mx-auto">
          Get unlimited access to live game streams, archived content, and exclusive features.
        </p>
      </div>

      {/* Subscription Active */}
      {subscription?.active && (
        <div className="bg-emerald-900/20 border border-emerald-900 p-6 text-center rounded-sm">
          <Check className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <h3 className="text-xl font-heading font-bold text-emerald-400">You're Subscribed!</h3>
          <p className="text-zinc-400 mt-2">Enjoy unlimited access to all content.</p>
        </div>
      )}

      {/* Plans */}
      {!subscription?.active && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              data-testid={`plan-${plan.id}`}
              className={`relative bg-zinc-900/50 border p-6 ${
                plan.popular 
                  ? 'border-yellow-500 ring-1 ring-yellow-500/20' 
                  : 'border-zinc-800'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 bg-yellow-500 text-black text-xs font-bold uppercase rounded-sm flex items-center gap-1">
                    <Star className="w-3 h-3" /> Best Value
                  </span>
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-heading font-bold mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-heading font-bold">${plan.price}</span>
                  <span className="text-zinc-500">/{plan.interval}</span>
                </div>
                {plan.interval === 'year' && (
                  <p className="text-sm text-emerald-400 mt-1">
                    Just {formatPrice(plan.price, plan.interval)}
                  </p>
                )}
              </div>
              
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loading}
                data-testid={`subscribe-${plan.id}`}
                className={`w-full font-bold uppercase ${
                  plan.popular
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-black'
                    : 'bg-white hover:bg-zinc-200 text-black'
                }`}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {loading ? 'Processing...' : 'Subscribe Now'}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 border-t border-zinc-800">
        <div className="text-center">
          <div className="w-12 h-12 bg-blue-900/20 rounded-sm flex items-center justify-center mx-auto mb-3">
            <Zap className="w-6 h-6 text-blue-400" />
          </div>
          <h4 className="font-heading font-bold mb-1">Live Streaming</h4>
          <p className="text-sm text-zinc-500">Watch games as they happen in real-time</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-purple-900/20 rounded-sm flex items-center justify-center mx-auto mb-3">
            <Star className="w-6 h-6 text-purple-400" />
          </div>
          <h4 className="font-heading font-bold mb-1">HD Quality</h4>
          <p className="text-sm text-zinc-500">Crystal clear video quality on all devices</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-emerald-900/20 rounded-sm flex items-center justify-center mx-auto mb-3">
            <Crown className="w-6 h-6 text-emerald-400" />
          </div>
          <h4 className="font-heading font-bold mb-1">Exclusive Content</h4>
          <p className="text-sm text-zinc-500">Access archived games anytime</p>
        </div>
      </div>
    </div>
  );
};
